import { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { clinicInfo } from "../data";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  const navItems = [
    {
      label: "Behandlinger",
      href: "/behandlinger",
      children: [
        { label: "Ryg", href: "/behandlinger/ryg" },
        { label: "Arme og ben", href: "/behandlinger/arme-og-ben" },
        { label: "Skulder", href: "/behandlinger/skulder" },
        { label: "Hofte", href: "/behandlinger/hofte" },
        { label: "Hovedpine", href: "/behandlinger/hovedpine" },
        { label: "Babyer & børn", href: "/behandlinger/babyer-og-boern" },
        { label: "Gravide", href: "/behandlinger/gravide" },
        { label: "Voksne & seniorer", href: "/behandlinger/voksne-og-seniorer" },
        { label: "Røntgen", href: "/behandlinger/roentgen" },
      ],
    },
    { label: "Personale", href: "/personale" },
    { label: "Praktisk info & priser", href: "/praktisk" },
    { label: "Kontakt", href: "/kontakt" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-brand-100 shadow-xs">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="https://assets.auxodev.dk/assets/783a3379-7262-480b-b55e-1bf6fb783275/kiropraktisk-assens-logo-08_2.png"
              alt="Kiropraktisk Klinik Assens"
              className="h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-102"
            />
          </Link>

          {/* Desktop Navigation (>= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-8">
            {navItems.map((item) => {
              if (item.children) {
                const isChildActive = item.children.some(
                  (child) => location.pathname === child.href
                );
                return (
                  <div
                    key={item.label}
                    className="relative"
                    ref={dropdownRef}
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    onMouseLeave={() => setIsDropdownOpen(false)}
                  >
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 py-2 cursor-pointer hover:text-brand-600 ${
                        isChildActive || location.pathname === item.href
                          ? "text-brand-700 font-semibold border-b-2 border-brand-600"
                          : "text-brand-800"
                      }`}
                    >
                      {item.label}
                      <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute left-0 mt-1 w-56 rounded-xl bg-white p-2 shadow-lg border border-brand-100 animate-in fade-in slide-in-from-top-2 duration-200 before:absolute before:-top-2 before:left-0 before:right-0 before:h-2 before:content-['']">
                        <Link
                          to={item.href}
                          className={`block rounded-lg px-4 py-2.5 text-sm transition-colors duration-150 hover:bg-brand-50 hover:text-brand-700 ${
                            location.pathname === item.href
                              ? "bg-brand-50 text-brand-700 font-medium"
                              : "text-brand-800"
                          }`}
                        >
                          Oversigt
                        </Link>
                        <div className="my-1 border-t border-brand-50" />
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className={`block rounded-lg px-4 py-2.5 text-sm transition-colors duration-150 hover:bg-brand-50 hover:text-brand-700 ${
                              location.pathname === child.href
                                ? "bg-brand-50 text-brand-700 font-medium"
                                : "text-brand-800"
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.label}
                  to={item.href}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors duration-200 py-2 hover:text-brand-600 ${
                      isActive
                        ? "text-brand-700 font-semibold border-b-2 border-brand-600"
                        : "text-brand-800"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {/* Desktop Booking CTA */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <a
              href={`tel:${clinicInfo.phone}`}
              className="text-sm font-medium text-brand-700 hover:text-brand-800 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone mr-2" />
              <span className="whitespace-nowrap">{clinicInfo.phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4")}</span>
            </a>
            <a
              href={clinicInfo.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 transition-all duration-200 hover:scale-102 active:scale-98 cursor-pointer"
            >
              Bestil tid online
            </a>
          </div>

          {/* Mobile Menu Button (< 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-4">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center rounded-lg p-2 text-brand-800 hover:bg-brand-50 hover:text-brand-600 focus:outline-hidden cursor-pointer"
              aria-label="Åbn/luk menu"
            >
              <i className={`fa-solid ${isOpen ? "fa-xmark" : "fa-bars"} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel (< 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden border-t border-brand-100 bg-white max-h-[calc(100vh-5rem)] overflow-y-auto animate-in slide-in-from-top duration-200">
          <div className="space-y-1 px-4 py-4">
            {navItems.map((item) => {
              if (item.children) {
                return (
                  <div key={item.label} className="space-y-1">
                    <div className="px-3 py-2 text-sm font-semibold text-brand-900 uppercase tracking-wider">
                      {item.label}
                    </div>
                    <Link
                      to={item.href}
                      className={`block rounded-lg px-6 py-2 text-sm font-medium transition-colors hover:bg-brand-50 hover:text-brand-700 ${
                        location.pathname === item.href ? "bg-brand-50 text-brand-700" : "text-brand-800"
                      }`}
                    >
                      Oversigt
                    </Link>
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.href}
                        className={`block rounded-lg px-6 py-2 text-sm font-medium transition-colors hover:bg-brand-50 hover:text-brand-700 ${
                          location.pathname === child.href ? "bg-brand-50 text-brand-700" : "text-brand-800"
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.label}
                  to={item.href}
                  className={({ isActive }) =>
                    `block rounded-lg px-3 py-2.5 text-base font-medium transition-colors hover:bg-brand-50 hover:text-brand-700 ${
                      isActive ? "bg-brand-50 text-brand-700 font-semibold" : "text-brand-800"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}

            <div className="border-t border-brand-50 pt-4 mt-4 space-y-3 px-3">
              <a
                href={`tel:${clinicInfo.phone}`}
                className="flex items-center text-brand-800 hover:text-brand-600 transition-colors"
              >
                <i className="fa-solid fa-phone mr-3 text-brand-600" />
                <span className="whitespace-nowrap">{clinicInfo.phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4")}</span>
              </a>
              <a
                href={`mailto:${clinicInfo.email}`}
                className="flex items-center text-brand-800 hover:text-brand-600 transition-colors"
              >
                <i className="fa-solid fa-envelope mr-3 text-brand-600" />
                <span className="whitespace-nowrap">{clinicInfo.email}</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
